import { Play, FlaskConical, AlertTriangle, CheckCircle2, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PREVIEW_STATUS, fmtV, fmtDate } from "./constants";

export function SyncTab({
  includeUnknown, onIncludeUnknownChange, canImport, syncing, period, onMockSync,
  currentSync, onApplySync, onCancelPreview,
  conflicts, canResolve, onResolveConflict,
  syncs, onOpenSyncDetail,
}) {
  const openConflicts = conflicts.filter((c) => c.status === "OPEN");
  return (
    <>
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <FlaskConical className="w-5 h-5 text-amber-700 mt-0.5" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-amber-900">Mock Connector — SOLO TEST / DEVELOPMENT</p>
              <p className="text-xs text-amber-800 leading-relaxed">
                Dataset fittizio (80 dipendenti) marcato JOBTIMEHR — MOCK. Non è mai una fonte production:
                serve solo a testare staging, mapping e preview.
              </p>
              <label className="flex items-center gap-2 mt-2 text-xs text-amber-900">
                <input type="checkbox" checked={includeUnknown} onChange={(e) => onIncludeUnknownChange(e.target.checked)} data-testid="mock-include-unknown" />
                Includi un tipo contratto non mappato (test TO_REVIEW)
              </label>
            </div>
          </div>
          {canImport && (
            <Button onClick={onMockSync} disabled={syncing || !period} variant="outline" className="border-amber-400 text-amber-900" data-testid="run-mock-sync">
              <Play className="w-4 h-4 mr-1" aria-hidden="true" /> {syncing ? "Sync in corso…" : "Esegui sync MOCK"}
            </Button>
          )}
        </CardContent>
      </Card>

      {currentSync?.preview && (
        <Card className="border-slate-200" data-testid="sync-preview-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              Anteprima sincronizzazione
              {currentSync.mock
                ? <Badge className="bg-amber-100 text-amber-900 border-amber-300">MOCK / TEST DATA</Badge>
                : <Badge className="bg-green-100 text-green-900 border-green-300">JOBTIMEHR (reale) · {currentSync.mode}</Badge>}
              {currentSync.locked_preview && <Badge className="bg-red-100 text-red-800 border-red-300">Periodo LOCKED — sola anteprima</Badge>}
              {currentSync.frozen_source && <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300">Anno congelato in JOBTIMEHR</Badge>}
            </CardTitle>
            <p className="text-xs text-slate-500">
              Ricevute {currentSync.received} sezioni · {currentSync.valid} metriche · {currentSync.conflicts_count} conflitti
              {currentSync.skipped?.length > 0 && ` · saltati: ${currentSync.skipped.map((s) => `${s.domain} (${s.reason})`).join(", ")}`}
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {currentSync.warnings?.length > 0 && (
              <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900 space-y-1" data-testid="sync-warnings">
                {currentSync.warnings.map((w, i) => <p key={i}>{w}</p>)}
              </div>
            )}
            {currentSync.unmapped?.length > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900" data-testid="sync-unmapped">
                {currentSync.unmapped.map((u, i) => (
                  <p key={i} className="flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5" aria-hidden="true" />
                    VALORE NON MAPPATO: {u.field} = "{u.value}" ({u.count} record) — TO_REVIEW, escluso dall'import. Configura il mapping.
                  </p>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <Table data-testid="sync-preview-table">
                <TableHeader>
                  <TableRow><TableHead>Metrica</TableHead><TableHead className="text-right">Attuale</TableHead><TableHead className="text-right">In ingresso</TableHead><TableHead>Stato</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {currentSync.preview.map((p) => {
                    const st = PREVIEW_STATUS[p.status];
                    return (
                      <TableRow key={p.metric_code} data-testid={`preview-${p.metric_code}`}>
                        <TableCell className="text-sm">{p.label}</TableCell>
                        <TableCell className="text-right text-sm text-slate-600">{fmtV(p.current)}</TableCell>
                        <TableCell className="text-right text-sm font-semibold">{fmtV(p.incoming)}</TableCell>
                        <TableCell><Badge variant="outline" className={`${st.cls} text-[10px]`}>{st.label}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="flex gap-2">
              {!currentSync.locked_preview && !currentSync.applied && (
                <Button className="bg-green-700 hover:bg-green-800" onClick={() => onApplySync(currentSync.id)} data-testid="sync-approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" aria-hidden="true" /> Approva sincronizzazione
                </Button>
              )}
              <Button variant="outline" onClick={onCancelPreview} data-testid="sync-ignore-btn">Annulla</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {openConflicts.length > 0 && (
        <Card className="border-red-200" data-testid="conflicts-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-red-900">Conflitti dati — nessuna sovrascrittura automatica su valori approvati</CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="conflicts-table">
              <TableHeader>
                <TableRow><TableHead>Metrica</TableHead><TableHead className="text-right">Valore approvato</TableHead><TableHead className="text-right">Valore in ingresso</TableHead><TableHead className="text-right">Risoluzione</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {openConflicts.map((c) => (
                  <TableRow key={c.id} data-testid={`conflict-${c.metric_code}`}>
                    <TableCell className="text-sm">{c.metric_code}</TableCell>
                    <TableCell className="text-right text-sm">{fmtV(c.current_value)}</TableCell>
                    <TableCell className="text-right text-sm font-semibold">{fmtV(c.incoming_value)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {canResolve && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => onResolveConflict(c.id, "KEEP_CURRENT")} data-testid={`conflict-keep-${c.metric_code}`}>Mantieni attuale</Button>
                          <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => onResolveConflict(c.id, "USE_INCOMING")} data-testid={`conflict-use-${c.metric_code}`}>Usa JOBTIMEHR</Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Storico sincronizzazioni (periodo {period?.year})</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table data-testid="syncs-table">
            <TableHeader>
              <TableRow><TableHead>Data</TableHead><TableHead>Fonte</TableHead><TableHead>Mode</TableHead><TableHead>Stato</TableHead><TableHead>Metriche</TableHead><TableHead>Applicata</TableHead><TableHead className="text-right">Dettagli</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {syncs.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-6" data-testid="syncs-empty">Nessuna sincronizzazione eseguita.</TableCell></TableRow>
              )}
              {syncs.map((s) => (
                <TableRow key={s.id} data-testid={`sync-row-${s.id.slice(0, 8)}`}>
                  <TableCell className="text-xs text-slate-600">{fmtDate(s.started_at)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={s.mock ? "bg-amber-50 text-amber-800 border-amber-200 text-[10px]" : "bg-green-50 text-green-800 border-green-200 text-[10px]"}>
                      {s.mock ? "MOCK" : "JOBTIMEHR"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{s.mode || "FULL"}</TableCell>
                  <TableCell className="text-sm">{s.status}</TableCell>
                  <TableCell className="text-sm">{s.valid ?? "—"}</TableCell>
                  <TableCell className="text-sm">{s.applied ? "Sì" : "No"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => onOpenSyncDetail(s.id)} data-testid={`sync-detail-${s.id.slice(0, 8)}`}>
                      <Eye className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
